import Button from "../../../components/UI/Button/button";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { errorHandler } from "../../../utils";
import {
  clientUploadLogo,
  companyProfile,
} from "../../../services/clientCalls";
import { convertToBase64 } from "../../../utils/functions";
import PanelHero from "../PanelHero/PanelHero";
import {
  IconAlertCircle,
  IconCloudUpload,
  IconPhoto,
  IconTrash,
} from "@tabler/icons-react";
import s from "../steps.module.css";

const ALLOWED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/svg+xml",
];
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

const Stage1 = () => {
  let navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [image, setImage] = useState(null);
  const [load, setLoad] = useState(false);
  const [loader, setLoader] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const getCompanyProfile = async () => {
      setLoader(true);
      try {
        const res = await companyProfile();
        if (isMounted) {
          setImage(res?.data?.data?.client?.clientLogo || null);
          setLoader(false);
        }
      } catch (error) {
        if (isMounted) {
          setLoader(false);
        }
      }
    };

    getCompanyProfile();

    return () => {
      isMounted = false;
    };
  }, []);

  const uploadLogo = async (e) => {
    e.preventDefault();

    if (image && typeof image === "string") {
      navigate({ pathname: "/client-onboarding", search: "stage=2" });
    } else {
      if (!image) {
        errorHandler("Please select a file", "Error");
        return;
      }
      setLoad(true);
      const base64Image = await convertToBase64(image);

      try {
        const data = {
          clientLogo: base64Image,
        };
        const res = await clientUploadLogo(data);
        if (res.data.success) {
          navigate({ pathname: "/client-onboarding", search: "stage=2" });
        }
        setLoad(false);
      } catch (error) {
        setLoad(false);
        error?.response?.data
          ? errorHandler(error?.response?.data?.errors, "Error")
          : errorHandler(
              "Network error: Something went wrong, Please check your internet connection and try again.",
              "Error",
            );
      }
    }
  };

  const handleFile = (file) => {
    setFileError("");
    if (!ALLOWED_TYPES.includes(file.type)) {
      setFileError("Unsupported type. Use PNG, JPG, WEBP, or SVG.");
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setFileError("File too large. Max 5 MB.");
      return;
    }
    setImage(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) handleFile(e.dataTransfer.files[0]);
  };

  const handleRemove = () => {
    setImage(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div>
      <PanelHero
        icon={<IconPhoto />}
        title="Company Logo"
        subtitle="Use this field to attach your company's logo"
      />

      <div className={s.panelBody}>
        {/* Preview */}
        {image && (
          <div className={s.logoPreviewRow}>
            <div className={s.logoPreviewThumb}>
              <img
                src={
                  typeof image === "string" ? image : URL.createObjectURL(image)
                }
                alt="Logo preview"
              />
            </div>
            <div className={s.logoPreviewMeta}>
              <>
                {typeof image === "string" ? (
                  <div className={s.logoPreviewName}>{image.name || ""}</div>
                ) : (
                  <>
                    <div className={s.logoPreviewName}>{image.name}</div>
                    <div className={s.logoPreviewSize}>
                      {(image.size / 1024).toFixed(1)} KB
                    </div>
                  </>
                )}
              </>

              <button className={s.btnRemove} onClick={handleRemove}>
                <IconTrash size={14} /> Remove
              </button>
            </div>
          </div>
        )}

        {!image && (
          <div
            className={`${s.uploadZone} ${dragOver ? s.uploadZoneDrag : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={(e) => {
                if (e.target.files.length) {
                  handleFile(e.target.files[0]);
                }
              }}
              style={{ display: "none" }}
            />
            <div className={s.uploadIconBox}>
              <IconCloudUpload />
            </div>
            <div className={s.uploadText1}>Drag and drop here</div>
            <div className={s.uploadText2}>OR</div>
            <div className={s.uploadText1}>
              <span className={s.uploadBrowse}>Browse files</span>
            </div>
          </div>
        )}

        {fileError && (
          <div
            style={{
              fontSize: "11.5px",
              color: "#dc2626",
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              gap: 4,
              marginBottom: 10,
            }}
          >
            <IconAlertCircle size={13} />
            {fileError}
          </div>
        )}

        <div className={s.logoFormatNote}>
          Note: the preferred image formats are JPEG and PNG. Max 5 MB.
        </div>
      </div>

      <div className={s.panelFooter}>
        <div className={s.footerNote} />
        <div className={s.footerActions}>
          <Button
            variant="outline"
            onClick={() =>
              navigate({ pathname: "/client-onboarding", search: "stage=2" })
            }
            disabled={load}
          >
            Skip
          </Button>
          <Button onClick={uploadLogo} disabled={image === null} loading={load}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Stage1;
